import { useCallback, useEffect, useRef, useState } from "react";
import type { Editor } from "@tiptap/react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { api, resolveAssetUrl } from "@/lib/api";
import { prepareHtmlForEditor } from "@/lib/html";
import MediaLibraryModal from "@/components/feature/MediaLibraryModal";

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  label?: string;
  placeholder?: string;
}

type ToolbarButton = {
  label: string;
  icon: string;
  action: () => void;
  active?: boolean;
  disabled?: boolean;
};

type UploadResult = { url: string; originalFilename?: string | null };

function Toolbar({ buttons }: { buttons: ToolbarButton[] }) {
  return (
    <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-gray-200 bg-[#f6f9fc] rounded-t-md">
      {buttons.map((btn) => (
        <button
          key={btn.label}
          type="button"
          title={btn.label}
          disabled={btn.disabled}
          onClick={btn.action}
          className={`w-8 h-8 flex items-center justify-center rounded text-sm transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ${
            btn.active
              ? "bg-[#1977cc] text-white"
              : "text-gray-600 hover:bg-white hover:text-[#1977cc]"
          }`}
        >
          <i className={btn.icon} />
        </button>
      ))}
    </div>
  );
}

async function uploadEditorImage(file: File): Promise<string> {
  if (!file.type.startsWith("image/")) {
    throw new Error("이미지 파일만 업로드할 수 있습니다.");
  }
  if (file.size > 5 * 1024 * 1024) {
    throw new Error("파일 크기는 5MB를 초과할 수 없습니다.");
  }
  const res = await api.upload<UploadResult>("/admin/files/images", file);
  if (!res.data?.url) throw new Error("업로드에 실패했습니다.");
  return res.data.url;
}

export default function RichTextEditor({
  value,
  onChange,
  label = "Content",
  placeholder = "내용을 입력하세요...",
}: RichTextEditorProps) {
  const [revision, setRevision] = useState(0);
  const [imageUploading, setImageUploading] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<Editor | null>(null);

  const insertImages = useCallback(async (ed: Editor, files: File[]) => {
      setImageUploading(true);
      try {
        for (const file of files) {
          const path = await uploadEditorImage(file);
          const src = resolveAssetUrl(path);
          ed.chain().focus().setImage({ src, alt: file.name }).run();
        }
      } catch (err: unknown) {
        alert(err instanceof Error ? err.message : "이미지 업로드에 실패했습니다.");
      } finally {
        setImageUploading(false);
      }
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Image.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: "editor-image rounded-lg max-w-full h-auto my-3",
        },
      }),
    ],
    content: prepareHtmlForEditor(value || ""),
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
    editorProps: {
      attributes: {
        class:
          "prose-editor min-h-[220px] px-4 py-3 text-sm text-[#2c4964] focus:outline-none",
        "data-placeholder": placeholder,
      },
      handleDrop: (_view, event, _slice, moved) => {
        if (moved || !event.dataTransfer?.files?.length) return false;
        const images = Array.from(event.dataTransfer.files).filter((f) =>
          f.type.startsWith("image/"),
        );
        if (images.length === 0) return false;
        event.preventDefault();
        const ed = editorRef.current;
        if (ed) void insertImages(ed, images);
        return true;
      },
      handlePaste: (_view, event) => {
        const images = Array.from(event.clipboardData?.files ?? []).filter((f) =>
          f.type.startsWith("image/"),
        );
        if (images.length === 0) return false;
        event.preventDefault();
        const ed = editorRef.current;
        if (ed) void insertImages(ed, images);
        return true;
      },
    },
  });

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  useEffect(() => {
    if (!editor) return;
    const onTransaction = () => setRevision((n) => n + 1);
    editor.on("transaction", onTransaction);
    return () => {
      editor.off("transaction", onTransaction);
    };
  }, [editor]);

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = prepareHtmlForEditor(value || "");
    if (current !== next) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [value, editor]);

  const pickImage = () => imageInputRef.current?.click();

  const onImageFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (!files.length || !editor) return;
    await insertImages(editor, files);
  };

  if (!editor) {
    return (
      <div className="rounded-md border border-gray-200 bg-gray-50 h-[260px] flex items-center justify-center text-sm text-gray-400">
        에디터 로딩 중...
      </div>
    );
  }

  const toolbarButtons: ToolbarButton[] = [
    {
      label: "이미지 업로드",
      icon: "ri-image-add-line",
      action: pickImage,
      disabled: imageUploading,
    },
    {
      label: "라이브러리에서 선택",
      icon: "ri-gallery-line",
      action: () => setLibraryOpen(true),
      disabled: imageUploading,
    },
    {
      label: "굵게",
      icon: "ri-bold",
      action: () => editor.chain().focus().toggleBold().run(),
      active: editor.isActive("bold"),
    },
    {
      label: "기울임",
      icon: "ri-italic",
      action: () => editor.chain().focus().toggleItalic().run(),
      active: editor.isActive("italic"),
    },
    {
      label: "제목 2",
      icon: "ri-h-2",
      action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      active: editor.isActive("heading", { level: 2 }),
    },
    {
      label: "제목 3",
      icon: "ri-h-3",
      action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      active: editor.isActive("heading", { level: 3 }),
    },
    {
      label: "글머리",
      icon: "ri-list-unordered",
      action: () => editor.chain().focus().toggleBulletList().run(),
      active: editor.isActive("bulletList"),
    },
    {
      label: "번호",
      icon: "ri-list-ordered",
      action: () => editor.chain().focus().toggleOrderedList().run(),
      active: editor.isActive("orderedList"),
    },
    {
      label: "인용",
      icon: "ri-double-quotes-l",
      action: () => editor.chain().focus().toggleBlockquote().run(),
      active: editor.isActive("blockquote"),
    },
    {
      label: "구분선",
      icon: "ri-separator",
      action: () => editor.chain().focus().setHorizontalRule().run(),
    },
    {
      label: "실행 취소",
      icon: "ri-arrow-go-back-line",
      action: () => editor.chain().focus().undo().run(),
    },
    {
      label: "다시 실행",
      icon: "ri-arrow-go-forward-line",
      action: () => editor.chain().focus().redo().run(),
    },
  ];

  return (
    <div>
      <label className="block text-sm font-medium text-[#2c4964] mb-1.5">{label}</label>
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={onImageFileChange}
      />
      <div className="rounded-md border border-gray-200 overflow-hidden bg-white focus-within:border-[#1977cc] focus-within:ring-1 focus-within:ring-[#1977cc]/20">
        <Toolbar key={revision} buttons={toolbarButtons} />
        {imageUploading && (
          <div className="px-3 py-1.5 text-xs text-[#1977cc] bg-[#f1f7fc] border-b border-gray-100 flex items-center gap-2">
            <div className="w-3 h-3 border-2 border-[#1977cc] border-t-transparent rounded-full animate-spin" />
            이미지 업로드 중...
          </div>
        )}
        <EditorContent editor={editor} />
      </div>
      <p className="mt-1.5 text-xs text-gray-400">
        툴바에서 이미지 업로드·라이브러리 선택, 드래그 앤 드롭, 붙여넣기로 본문에 삽입할 수 있습니다.
      </p>

      <MediaLibraryModal
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        category="image"
        onSelect={(url, meta) => {
          if (!editor) return;
          const src = resolveAssetUrl(url);
          editor.chain().focus().setImage({ src, alt: meta?.originalFilename || "" }).run();
        }}
      />
    </div>
  );
}
